import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { environment } from '../../../environments/environment';
import { DynamicCssService } from '../../services/dynamic-css.service';
import { CommonModule } from '@angular/common';
import { IssueTrackerUserFilterService } from '../../services/issue-tracker-user-filter.service';
import { IssueTrackerUserFilterDTO } from '../../models/issue-tracker-user-filter-dto';
import Swal from 'sweetalert2';
import { ToastrService } from 'ngx-toastr';
import { IssueTrackerIncidenceFilterDTO } from '../../models/issue-tracker-incidence-dto';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { AuthService } from '../../services/auth.service';
import { UserService } from '../../services/user.service';
import { IssueTrackerUserDTO } from '../../models/issue-tracker-user-dto';
import { AdminLayoutMode } from '../../config/layout.config';

interface MenuItem {
  label: string;
  route: string;
  iconClass: string;
}

@Component({
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    CommonModule
  ],
  selector: 'app-default-layout',
  standalone: true,
  templateUrl: './default-layout.component.html',
  styles: [
    `
    body {
      transition: opacity 0.3s ease-in-out;
    }
    `
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DefaultLayoutComponent implements OnInit, OnDestroy {
  readonly layoutMode: AdminLayoutMode = environment.adminLayoutMode ?? 'sidebar';
  readonly useHorizontalNav = this.layoutMode === 'horizontal';

  pageTitle = 'Gestión de consultas';
  isSidebarCollapsed = false;
  menuState: Record<string, boolean> = {};
  private readonly auth = inject(AuthService);
  private routerSubscription?: Subscription;
  private readonly collapseBreakpoint = 767.98;
  private readonly menuRoutePrefixes = {
    'home': ['/home'],
    'admin': ['/admin'],
    'statistics': ['/statistics']
  };
  private readonly pageTitles = [
    { prefix: '/home', title: 'Gestión de Consultas' },
    { prefix: '/admin/client', title: 'Clientes / Proyectos' },
    { prefix: '/admin/template', title: 'Plantillas' },
    { prefix: '/statistics', title: 'Estadísticas' },
  ];

  userFilters = signal<IssueTrackerUserFilterDTO[]>([]);
  userInfo = signal<IssueTrackerUserDTO | null>(null);
  readonly currentYear = new Date().getFullYear();

  readonly menuItems: MenuItem[] = [
    { label: 'Consultas', route: '/home', iconClass: 'bi bi-inboxes' },
    { label: 'Clientes / Proyectos', route: '/admin/client', iconClass: 'bi bi-file-earmark-text' },
    { label: 'Plantillas', route: '/admin/template', iconClass: 'bi bi-file-earmark-richtext' },
    { label: 'Estadísticas', route: '/statistics', iconClass: 'bi bi-graph-up' }
  ];
  private environment = environment;
  /** Nombres de tema para el widget (archivos en assets/css/help-widget-themes sin prefijo ni extensión) */
  widgetThemeOptions: string[] = [];

  constructor(
    private router: Router,
    private dynamicCss: DynamicCssService,
    private userFilterService: IssueTrackerUserFilterService,
    private toastr: ToastrService,
    private http: HttpClient,
    private userService: UserService
  ) { }

  ngOnInit(): void {
    this.userService.getUserData().subscribe(user => this.userInfo.set(user));
    this.getUserFilters();
    if (!this.useHorizontalNav) {
      this.isSidebarCollapsed = window.innerWidth <= this.collapseBreakpoint;
    }
    document.body.style.opacity = '0';
    this.dynamicCss.loadStylesheetLink('admin.css', 'admin-css').then(() => {
      document.body.style.opacity = '1';
    });
    this.dynamicCss.loadStylesheetLink(
      'https://cdnjs.cloudflare.com/ajax/libs/bootstrap-icons/1.11.0/font/bootstrap-icons.min.css',
      'bootstrap-icons'
    );
    this.syncMenusWithRoute(this.router.url);
    this.updatePageTitle(this.router.url);
    this.routerSubscription = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(event => {
        this.syncMenusWithRoute(event.urlAfterRedirects);
        this.updatePageTitle(event.urlAfterRedirects);
      });
    this.loadWidgetThemeOptions();
  }

  ngOnDestroy() {
    this.routerSubscription?.unsubscribe();
  }

  toggleSidebar(): void {
    if (this.useHorizontalNav) {
      return;
    }
    this.isSidebarCollapsed = !this.isSidebarCollapsed;
  }

  toggleMenu(key: string): void {
    const submenu: HTMLElement = document.querySelector(`[data-ap-parent="${key}"]`);
    if (submenu) {
      let maxHeight = 0;
      const links = submenu.querySelectorAll('.nav-link');
      links.forEach((link: HTMLElement) => {
        maxHeight += link.offsetHeight;
      });
      submenu.style.maxHeight = `${maxHeight}px`;
    }
    this.menuState[key] = !this.isMenuOpen(key);

    if (!this.menuState[key]) {
      submenu.style.maxHeight = '0';
    }
  }

  isMenuOpen(key: string): boolean {
    return Boolean(this.menuState[key]);
  }

  private syncMenusWithRoute(url: string): void {
    let hasOpenMenu = false;
    for (const key of Object.keys(this.menuRoutePrefixes)) {
      const isOpen = this.menuRoutePrefixes[key].some(prefix => url.startsWith(prefix));
      this.menuState[key] = isOpen;
      console.log(key, isOpen);
      if (isOpen) {
        hasOpenMenu = true;
        break;
      }
    }
    if (!hasOpenMenu) {
      this.menuState['home'] = true;
    }
  }

  private updatePageTitle(url: string): void {
    const match = this.pageTitles.find(item => url.startsWith(item.prefix));
    this.pageTitle = match ? match.title : 'Gestión de consultas';
    document.title = `SIM Issue Tracker - ${this.pageTitle}`;
  }

  protected login(): void {
    this.auth.login();
  }

  protected logout(): void {
    this.auth.logout();
  }

  getUserFilters(): void {
    this.userFilterService.getAll().subscribe({
      next: (res) => {
        this.userFilters.set(res);
        this.userFilterService.setUserFilters(res);
        this.userFilterService.getUserFilters().subscribe({
          next: (res) => {
            this.userFilters.set(res);
          }
        });
      }
    });
  }

  applyFilter(filter: IssueTrackerUserFilterDTO): void {
    const filterData = JSON.parse(filter.FilterData) as IssueTrackerIncidenceFilterDTO;
    this.userFilterService.setActiveFilter(filterData);
    this.router.navigate(['/home']);
  }

  deleteFilter(filter: IssueTrackerUserFilterDTO, event: Event): void {
    event.stopPropagation(); // Prevenir que se active el filtro al hacer clic en eliminar

    Swal.fire({
      title: '¿Estás seguro?',
      text: `¿Deseas eliminar el filtro "${filter.FilterName}"?`,
      icon: 'warning',
      buttonsStyling: false,
      customClass: {
        cancelButton: 'btn-outline-black',
        confirmButton: 'btn-red'
      },
      showCancelButton: true,
      allowOutsideClick: false,
      confirmButtonText: 'Eliminar <i class="fa-solid fa-trash-can"></i>',
      cancelButtonText: 'Cancelar <i class="fa-solid fa-xmark"></i>',
      reverseButtons: true,
      focusCancel: true
    }).then((result) => {
      if (result.isConfirmed) {
        this.userFilterService.delete(filter.IdFilter).subscribe({
          next: () => {
            // Actualizar la lista de filtros removiendo el eliminado
            this.userFilterService.removeUserFilter(filter);
            this.toastr.success("El filtro se ha eliminado correctamente");
            Swal.close();
          },
          error: (err) => {
            Swal.fire({
              icon: 'error',
              title: 'Error al eliminar',
              text: 'Ocurrió un error al eliminar el filtro'
            });
          }
        });
      }
    });
  }

  /** Carga la lista de temas del widget desde assets (archivos en help-widget-themes) */
  loadWidgetThemeOptions() {
    this.http.get<string[]>('assets/css/help-widget-themes/themes-list.json').subscribe({
      next: (list) => { this.widgetThemeOptions = list || []; },
      error: () => { this.widgetThemeOptions = []; }
    });
  }



  previewWidget() {
    const themeOptions = this.widgetThemeOptions.length > 0
      ? this.widgetThemeOptions.reduce((acc, t) => ({ ...acc, [t]: t }), {} as Record<string, string>)
      : { 'default': 'default' };

    Swal.fire({
      html:
        '<div class="text-start mb-3">' +
        '<h4>Vista previa del widget</h4>' +
        '<div class="form-group mb-2">' +
        '<label class="form-label">Tema</label>' +
        '<select id="swal-theme" class="form-select">' +
        Object.keys(themeOptions).map(t => `<option value="${t}">${t}</option>`).join('') +
        '</select>' +
        '</div>' +
        '<div class="form-group mb-2">' +
        '<label class="form-label">Título</label>' +
        '<input id="swal-title" class="form-control" value="Ayuda" placeholder="Título">' +
        '</div>' +
        '<div class="form-group mb-2">' +
        '<label class="form-label">Tagline</label>' +
        '<input id="swal-tagline" class="form-control" value="Te acompañamos en cada paso" placeholder="Tagline">' +
        '</div>' +
        '<div class="form-group mb-2">' +
        '<label class="form-label">Tipo de botón</label>' +
        '<select id="swal-button-type" class="form-select">' +
        '<option value="rounded" selected>Redondo (iconos)</option>' +
        '<option value="pill">Pastilla (texto)</option>' +
        '</select>' +
        '</div>' +
        '<div class="form-group mb-2" id="swal-button-text-group" style="display:none">' +
        '<label class="form-label">Texto del botón (modo pastilla)</label>' +
        '<input id="swal-button-text" class="form-control" value="Ayuda" placeholder="Ayuda">' +
        '</div>' +
        '<div class="form-group mb-2">' +
        '<label class="form-label">Tooltip del botón</label>' +
        '<input id="swal-button-tooltip" class="form-control" value="Mostrar/Ocultar Ayuda" placeholder="Mostrar/Ocultar Ayuda">' +
        '<small class="text-muted d-block mt-1">Título y descripción accesible del launcher.</small>' +
        '</div>' +
        '</div>' +
        '<div class="alert alert-info mb-0 p-2"><small>Tenga en cuenta que se trata de una vista previa. No se enviará ninguna consulta.</small></div>',
      buttonsStyling: false,
      customClass: {
        cancelButton: 'btn-outline-black',
        confirmButton: 'btn-red'
      },
      showCancelButton: true,
      confirmButtonText: 'Aceptar',
      cancelButtonText: 'Cancelar <i class="fa-solid fa-xmark"></i>',
      reverseButtons: true,
      focusCancel: false,
      didOpen: () => {
        const typeSelect = document.getElementById('swal-button-type') as HTMLSelectElement | null;
        const textGroup = document.getElementById('swal-button-text-group');
        const syncButtonTextVisibility = () => {
          if (textGroup && typeSelect) {
            textGroup.style.display = typeSelect.value === 'pill' ? '' : 'none';
          }
        };
        typeSelect?.addEventListener('change', syncButtonTextVisibility);
        syncButtonTextVisibility();
      },
      preConfirm: () => {
        const theme = (document.getElementById('swal-theme') as HTMLSelectElement)?.value?.trim();
        const title = (document.getElementById('swal-title') as HTMLInputElement)?.value?.trim();
        const tagline = (document.getElementById('swal-tagline') as HTMLInputElement)?.value?.trim();
        const buttonType = (document.getElementById('swal-button-type') as HTMLSelectElement)?.value?.trim() || 'rounded';
        const buttonText = (document.getElementById('swal-button-text') as HTMLInputElement)?.value?.trim();
        const buttonTooltip = (document.getElementById('swal-button-tooltip') as HTMLInputElement)?.value?.trim();
        if (!title) {
          Swal.showValidationMessage('El título es obligatorio');
          return false;
        }
        if (buttonType === 'pill' && !buttonText) {
          Swal.showValidationMessage('El texto del botón es obligatorio en modo pastilla');
          return false;
        }
        return {
          theme: theme || 'default',
          title,
          tagline: tagline || '',
          buttonType,
          buttonText: buttonText || '',
          buttonTooltip: buttonTooltip || 'Mostrar/Ocultar Ayuda',
        };
      }
    }).then((result) => {
      if (result.isConfirmed && result.value) {
        // Eliminar el widget previo si existe
        this.destroyPreviewWidget();

        const { theme, title, tagline, buttonType, buttonText, buttonTooltip } = result.value;
        const script = document.createElement('script');
        script.src = this.environment.urlApp + 'assets/js/widget.js';
        script.setAttribute('data-sit-key', 'preview');
        script.setAttribute('data-sit-user-data', `email=empleado@empresa.xyz`);
        script.setAttribute('data-sit-title', title);
        script.setAttribute('data-sit-tagline', tagline);
        script.setAttribute('data-sit-position', '10,0');
        script.setAttribute('data-sit-theme', theme);
        script.setAttribute('data-sit-app-url', this.environment.urlApp);
        script.setAttribute('data-sit-button-type', buttonType);
        script.setAttribute('data-sit-button-tooltip', buttonTooltip);
        if (buttonType === 'pill' && buttonText) {
          script.setAttribute('data-sit-button-text', buttonText);
        }
        script.async = true;
        document.head.appendChild(script);
      }
    });
  }

  destroyPreviewWidget() {
    const existingScript = document.querySelector('script[data-sit-key]');
    if (existingScript) {
      existingScript.parentNode?.removeChild(existingScript);
    }
    document.querySelectorAll('[id^="sit-iframe-"]').forEach((el) => el.parentNode?.removeChild(el));
    document.querySelectorAll('.sit-widget-toggle').forEach((el) => el.parentNode?.removeChild(el));
    document.querySelectorAll('style[id^="sit-widget-launcher-style-"]').forEach((el) => el.parentNode?.removeChild(el));
  }
}

